import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, FormControl, InputLabel, Select, MenuItem, Box, Typography,
  IconButton, Alert, Paper, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import { fileService, truckService } from '../api/api';
import { confirmOdometerUpdate } from '../utils/odometer';
import { VEHICLE_CATEGORIES } from '../utils/vehicleCategories';
import {
  OTHER,
  baseTruckFormState,
  statusOptions,
  transmissionOptions,
  rearDoorTypeOptions,
  lightingOptions,
  seatOptions,
  conditionOptions,
  liftgateOptions,
  lightbarOptions,
  isolatorSwitchOption,
  trickleChargerOptions,
  batterySystemOptions,
  liftgateStatusOptions,
  fuelTankTypeOptions,
  lightYearOptions,
  interiorLightControlOptions,
  taillightDesignOptions,
  taillightTypeOptions,
  boxlightDesignOptions,
  boxlightTypeOptions,
  otherFieldMap,
  parseFuelTankCapacities,
  extractInvoiceFilename,
  invoiceDisplayName,
  parseInvoiceUrls,
  serializeInvoiceUrls,
} from './truckFormConfig';

export default function EditTruckDialog({ open, onClose, truck, onTruckUpdated }) {
  const [formData, setFormData] = useState(baseTruckFormState);
  const [fuelTankCapacities, setFuelTankCapacities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [invoicePreviews, setInvoicePreviews] = useState([]);
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [existingPhoto, setExistingPhoto] = useState(null);
  const [extraPhotoFiles, setExtraPhotoFiles] = useState([]);
  const [extraPhotoPreviews, setExtraPhotoPreviews] = useState([]);

  const renderPlaceholder = (placeholder) => (selected) => {
    if (!selected) return <span style={{ color: "#9e9e9e" }}>{placeholder}</span>;
    return selected;
  };

  const resolvedInitial = useMemo(() => {
    const next = { ...baseTruckFormState };
    if (!truck) return next;

    Object.keys(next).forEach((k) => {
      if (truck[k] !== undefined && truck[k] !== null) next[k] = truck[k];
    });

    next.year = truck.year || new Date().getFullYear();
    next.status = truck.status || 'Active';
    next.rear_door_type = truck.rear_door_type || truck.tailgate || '';

    return next;
  }, [truck]);

  useEffect(() => {
    if (!open) return;
    setFormData(resolvedInitial);
    setFuelTankCapacities(parseFuelTankCapacities(truck?.fuel_tank_capacities));
    setError('');
    setInvoiceFiles([]);
    setInvoicePreviews([]);
    setExistingInvoices(parseInvoiceUrls(truck?.invoice_url));
    setPhotoFile(null);
    setPhotoPreview(null);
    setExistingPhoto(truck?.photo_url || null);
    setExtraPhotoFiles([]);
    setExtraPhotoPreviews([]);
  }, [open, resolvedInitial, truck]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and WEBP are allowed.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setPhotoFile(file);
    setExistingPhoto(null);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleRemovePhoto = async () => {
    if (existingPhoto) {
      const filename = extractInvoiceFilename(existingPhoto);
      if (filename) {
        try {
          await axios.delete(`/api/upload/photo/truck/${encodeURIComponent(filename)}`);
        } catch (err) {
          console.error('Error deleting photo:', err);
        }
      }
    }
    setPhotoFile(null);
    setPhotoPreview(null);
    setExistingPhoto(null);
    setFormData((prev) => ({ ...prev, photo_url: '' }));
  };

  const handleExtraPhotosUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Only JPEG, PNG, GIF, and WEBP are allowed.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File too large. Maximum size is 10MB per photo.');
        return;
      }
    }

    const previews = files.map((f) => URL.createObjectURL(f));
    setExtraPhotoFiles(files);
    setExtraPhotoPreviews(previews);
  };

  const clearExtraPhotos = () => {
    extraPhotoPreviews.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch { /* noop */ }
    });
    setExtraPhotoFiles([]);
    setExtraPhotoPreviews([]);
  };

  const handleInvoiceUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    const invalidFile = files.find((file) => !allowedTypes.includes(file.type));
    if (invalidFile) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and PDF are allowed.');
      return;
    }

    if (files.some((file) => file.size > 10 * 1024 * 1024)) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setInvoiceFiles((prev) => [...prev, ...files]);
    setInvoicePreviews((prev) => [
      ...prev,
      ...files.map((file) => (file.type.startsWith('image/') ? URL.createObjectURL(file) : 'pdf')),
    ]);
    e.target.value = '';
  };

  const handleRemoveExistingInvoice = async (invoiceUrl) => {
    const filename = extractInvoiceFilename(invoiceUrl);
    if (filename) {
      try {
        await fileService.deleteInvoice(filename);
      } catch (err) {
        console.error('Error deleting invoice:', err);
      }
    }
    setExistingInvoices((prev) => prev.filter((url) => url !== invoiceUrl));
  };

  const handleRemovePendingInvoice = (index) => {
    setInvoicePreviews((prev) => {
      const preview = prev[index];
      if (preview && preview !== 'pdf') {
        try { URL.revokeObjectURL(preview); } catch { /* noop */ }
      }
      return prev.filter((_, i) => i !== index);
    });
    setInvoiceFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      const otherName = otherFieldMap[name];
      if (otherName) {
        if (value !== OTHER) next[otherName] = "";
        else if (next[otherName] == null) next[otherName] = "";
      }
      return next;
    });

    if (name === "fuel_tank_number") {
      const count = parseInt(value, 10) || 0;
      setFuelTankCapacities((prev) => {
        const newArray = [...prev];
        newArray.length = count;
        return newArray.fill("", prev.length);
      });
    }
  };

  const handleTankCapacityChange = (index, value) => {
    const updated = [...fuelTankCapacities];
    updated[index] = value;
    setFuelTankCapacities(updated);
  };

  const handleSubmit = async () => {
    if (!truck) return;

    if (!formData.name || !formData.make || !formData.model) {
      setError('Please fill in required fields: Name, Make, and Model');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let photoUrl = existingPhoto;
      const invoiceUrls = [...existingInvoices];

      if (photoFile) {
        setPhotoUploading(true);
        const uploadPhotoData = new FormData();
        uploadPhotoData.append('photo', photoFile);

        const uploadPhotoResponse = await axios.post('/api/upload/photo/truck', uploadPhotoData);
        photoUrl = uploadPhotoResponse.data.photoPath;
        setPhotoUploading(false);
      }

      if (invoiceFiles.length) {
        setUploading(true);
        for (const invoiceFile of invoiceFiles) {
          const uploadResponse = await fileService.uploadInvoice(invoiceFile);
          if (uploadResponse.data.invoicePath) invoiceUrls.push(uploadResponse.data.invoicePath);
        }
        setUploading(false);
      }

      if (extraPhotoFiles.length) {
        await truckService.uploadPhotos(truck.id, extraPhotoFiles);
        clearExtraPhotos();
      }

      const cleanData = {};
      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        if (value === '' || value === null || value === undefined) {
          cleanData[key] = null;
        } else if (key === 'year' || key === 'odometer') {
          const parsed = parseInt(value.toString().replace(/[^0-9]/g, ''), 10);
          cleanData[key] = isNaN(parsed) ? null : parsed;
        } else if (key === 'estimated_value') {
          const parsed = parseFloat(value.toString().replace(/[^0-9.]/g, ''));
          cleanData[key] = Number.isFinite(parsed) ? parsed : null;
        } else {
          cleanData[key] = value;
        }
      });

      if (!cleanData.year) cleanData.year = new Date().getFullYear();
      cleanData.invoice_url = serializeInvoiceUrls(invoiceUrls);
      cleanData.photo_url = photoUrl || null;
      cleanData.fuel_tank_capacities = fuelTankCapacities;

      if (!await confirmOdometerUpdate({
        currentValue: truck.odometer,
        nextValue: cleanData.odometer,
        context: 'update this vehicle odometer reading',
      })) {
        setLoading(false);
        return;
      }

      const response = await truckService.update(truck.id, cleanData);
      if (onTruckUpdated) onTruckUpdated(response.data);
      onClose();
    } catch (err) {
      console.error('Error updating truck:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to update truck. Please try again.';
      setError(msg);
      setUploading(false);
      setPhotoUploading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalShippingIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Edit Vehicle</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#e5f3fa', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Truck Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Truck A"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Make *"
                name="make"
                value={formData.make}
                onChange={handleChange}
                placeholder="e.g., Hino, Scania"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Model *"
                name="model"
                value={formData.model}
                onChange={handleChange}
                placeholder="e.g., H1, R500"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Important Notes"
                name="important_notes"
                value={formData.important_notes || ''}
                onChange={handleChange}
                placeholder="Any important notes for this truck..."
                size="small"
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Vehicle Row Color"
                name="row_color"
                type="color"
                value={formData.row_color || '#ffffff'}
                onChange={handleChange}
                size="small"
                helperText="Custom color on Vehicles page"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Year"
                name="year"
                type="number"
                value={formData.year}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Serial Number"
                name="serial_number"
                value={formData.serial_number || ''}
                onChange={handleChange}
                placeholder="e.g., 1HTMKADN43H561234"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="License Plate"
                name="license_plate"
                value={formData.license_plate}
                onChange={handleChange}
                placeholder="e.g., ABC123"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Odometer"
                name="odometer"
                value={formData.odometer}
                onChange={handleChange}
                placeholder="e.g., 125,450 km"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Estimated Value"
                name="estimated_value"
                value={formData.estimated_value || ''}
                onChange={handleChange}
                placeholder="e.g., 45000"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category || ''}
                  onChange={handleChange}
                  label="Category"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#9e9e9e" }}>Auto (detect)</span>;
                    }
                    return VEHICLE_CATEGORIES.find((c) => c.id === selected)?.label || selected;
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      whiteSpace: 'normal',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxWidth: 520,
                        '& .MuiMenuItem-root': { whiteSpace: 'normal' },
                      },
                    },
                  }}
                >
                  <MenuItem value=""><em>Auto (detect)</em></MenuItem>
                  {VEHICLE_CATEGORIES.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#fbf0e6', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#e65100', mb: 2 }}>
            Mechanical Specifications
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Front Axle" name="front_axle" value={formData.front_axle} onChange={handleChange} placeholder="e.g., 308 FRONT AXLE" size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="rear_door_type"
                  value={formData.rear_door_type || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Rear Door Type")}
                >
                  <MenuItem value="" disabled>Rear Door Type</MenuItem>
                  {rearDoorTypeOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="transmission"
                  value={formData.transmission || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Select Transmission")}
                >
                  <MenuItem value="" disabled>
                    Select Transmission
                  </MenuItem>
                  {transmissionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Box Length (ft)" name="box_length" value={formData.box_length} onChange={handleChange} placeholder="e.g., 24" size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="fuel_tank_number"
                  value={formData.fuel_tank_number || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Number of Fuel Tanks")}
                >
                  <MenuItem value="" disabled>Number of Fuel Tanks</MenuItem>
                  <MenuItem value={1}>1 Tank</MenuItem>
                  <MenuItem value={2}>2 Tanks</MenuItem>
                  <MenuItem value={3}>3 Tanks</MenuItem>
                  <MenuItem value={4}>4 Tanks</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {fuelTankCapacities.map((capacity, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <TextField
                  fullWidth
                  label={`Fuel Tank ${index + 1} Capacity (Litres)`}
                  value={capacity || ""}
                  onChange={(e) => handleTankCapacityChange(index, e.target.value)}
                  placeholder="e.g., 200"
                  size="small"
                  type="number"
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="fuel_tank_type"
                  value={formData.fuel_tank_type || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Fuel Tank Type")}
                >
                  <MenuItem value="" disabled>
                    Fuel Tank Type
                  </MenuItem>
                  {fuelTankTypeOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Box" name="box" value={formData.box} onChange={handleChange} placeholder="e.g., Original box" size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="seats"
                  value={formData.seats || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Seat Type")}
                >
                  <MenuItem value="" disabled>
                    Seat Type
                  </MenuItem>
                  {seatOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Tires" name="tires" value={formData.tires} onChange={handleChange} placeholder="e.g., Michelin 295/80R22.5" size="small" />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f2e8fa', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#530b75', mb: 2 }}>
            Electrical
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="tricklecharger"
                  value={formData.tricklecharger || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Trickle Charger Installed?")}
                >
                  <MenuItem value="" disabled>
                    Trickle Charger
                  </MenuItem>
                  {trickleChargerOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="battery"
                  value={formData.battery || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Select Battery System Type")}
                >
                  <MenuItem value="" disabled>
                    Select Battery System Type
                  </MenuItem>
                  {batterySystemOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={10} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="isolatorSwitch"
                  value={formData.isolatorSwitch || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Battery Disconnect (Isolator) Switch Present?")}
                >
                  <MenuItem value="" disabled>
                    Battery Disconnect (Isolator) Switch
                  </MenuItem>
                  {isolatorSwitchOption.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#e5f3fa', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
            Lights
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="headlight_type"
                  value={formData.headlight_type || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Headlight Type")}
                >
                  <MenuItem value="" disabled>
                    Head Lights
                  </MenuItem>
                  {lightingOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
                {formData.headlight_type === OTHER && (
                  <TextField
                    fullWidth
                    name="headlight_type_other"
                    value={formData.headlight_type_other || ""}
                    onChange={handleChange}
                    placeholder="Specify Headlight Type"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="headlights_design_year"
                  value={formData.headlights_design_year || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Head Light Design Year")}
                >
                  <MenuItem value="" disabled>
                    Head Light Design Year
                  </MenuItem>
                  {lightYearOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
                {formData.headlights_design_year === OTHER && (
                  <TextField
                    fullWidth
                    name="headlights_design_year_other"
                    value={formData.headlights_design_year_other || ""}
                    onChange={handleChange}
                    placeholder="Specify Design Year"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="lightbar"
                  value={formData.lightbar || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Fog Light Bar Present?")}
                >
                  <MenuItem value="" disabled>
                    Fog Light Bar
                  </MenuItem>
                  {lightbarOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="interior_light_control_location"
                  value={formData.interior_light_control_location || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Cargo Box Interior Control Location")}
                >
                  <MenuItem value="" disabled>
                    Select Box Interior Control Location
                  </MenuItem>
                  {interiorLightControlOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
                {formData.interior_light_control_location === OTHER && (
                  <TextField
                    fullWidth
                    name="interior_light_control_location_other"
                    value={formData.interior_light_control_location_other || ""}
                    onChange={handleChange}
                    placeholder="Specify Cargo Box Interior Control Location"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#e5f3fa', borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="taillightDesign"
                  value={formData.taillightDesign || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Tail Light Design")}
                >
                  <MenuItem value="" disabled>
                    Tail Lights
                  </MenuItem>
                  {taillightDesignOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
                {formData.taillightDesign === OTHER && (
                  <TextField
                    fullWidth
                    name="taillightDesign_other"
                    value={formData.taillightDesign_other || ""}
                    onChange={handleChange}
                    placeholder="Specify Tail Light Design"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="taillightType"
                  value={formData.taillightType || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Taillight Type")}
                >
                  <MenuItem value="" disabled>
                    Tail Lights
                  </MenuItem>
                  {taillightTypeOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
                {formData.taillightType === OTHER && (
                  <TextField
                    fullWidth
                    name="taillightType_other"
                    value={formData.taillightType_other || ""}
                    onChange={handleChange}
                    placeholder="Specify Tail Light Type"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#e5f3fa', borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="boxlightDesign"
                  value={formData.boxlightDesign || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Box Light Design")}
                >
                  <MenuItem value="" disabled>
                    Box Lights
                  </MenuItem>
                  {boxlightDesignOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
                {formData.boxlightDesign === OTHER && (
                  <TextField
                    fullWidth
                    name="boxlightDesign_other"
                    value={formData.boxlightDesign_other || ""}
                    onChange={handleChange}
                    placeholder="Specify Box Light Design"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="boxlightType"
                  value={formData.boxlightType || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Box Light Type")}
                >
                  <MenuItem value="" disabled>
                    Box Lights
                  </MenuItem>
                  {boxlightTypeOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#dff9ee', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#0b756a', mb: 2 }}>
            Equipment
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={10} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="liftgate"
                  value={formData.liftgate || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Liftgate Present?")}
                >
                  <MenuItem value="" disabled>
                    Select Liftgate
                  </MenuItem>
                  {liftgateOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  name="liftgate_status"
                  value={formData.liftgate_status || ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Liftgate Status")}
                >
                  <MenuItem value="" disabled>
                    Liftgate Status
                  </MenuItem>
                  {liftgateStatusOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, backgroundColor: '#f9e9ee', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#c2185b', mb: 2 }}>
            Condition Notes
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  name="frame_condition"
                  value={formData.frame_condition}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Frame Condition")}
                >
                  <MenuItem value="" disabled>Frame Condition</MenuItem>
                  {conditionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  name="cab_condition"
                  value={formData.cab_condition}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Cab Condition")}
                >
                  <MenuItem value="" disabled>Cab Condition</MenuItem>
                  {conditionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  name="rear_door_condition"
                  value={formData.rear_door_condition}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Rear Door Condition")}
                >
                  <MenuItem value="" disabled>Rear Door Condition</MenuItem>
                  {conditionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  name="hood_condition"
                  value={formData.hood_condition}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Hood Condition")}
                >
                  <MenuItem value="" disabled>Hood Condition</MenuItem>
                  {conditionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  name="clutch"
                  value={formData.clutch}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Clutch Condition")}
                >
                  <MenuItem value="" disabled>Clutch Condition</MenuItem>
                  {conditionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  name="headlight_condition"
                  value={formData.headlight_condition}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Headlight(s) Condition")}
                >
                  <MenuItem value="" disabled>Headlight(s) Condition</MenuItem>
                  {conditionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  name="taillight_condition"
                  value={formData.taillight_condition}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Tail Light(s) Condition")}
                >
                  <MenuItem value="" disabled>Tail Light(s) Condition</MenuItem>
                  {conditionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  name="boxlight_condition"
                  value={formData.boxlight_condition}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={renderPlaceholder("Box Light(s) Condition")}
                >
                  <MenuItem value="" disabled>Box Light(s) Condition</MenuItem>
                  {conditionOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mt: 3, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon /> Cover Photo (Optional)
          </Typography>

          {!photoFile && !existingPhoto ? (
            <Box
              component="label"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #1976d2',
                borderRadius: 2,
                p: 3,
                cursor: 'pointer',
                backgroundColor: '#e3f2fd',
                '&:hover': { backgroundColor: '#bbdefb' }
              }}
            >
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              <CloudUploadIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                Click to upload a truck photo (JPEG, PNG, GIF, WEBP — Max 10MB)
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box
                component="img"
                src={photoPreview || existingPhoto}
                alt="Truck"
                sx={{ maxWidth: 180, maxHeight: 120, borderRadius: 1, border: '1px solid #e0e0e0' }}
              />
              {existingPhoto && !photoFile && (
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  href={existingPhoto}
                  target="_blank"
                  sx={{ borderRadius: 2 }}
                >
                  View
                </Button>
              )}
              {existingPhoto && !photoFile && (
                <Box component="label" sx={{ cursor: 'pointer' }}>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} sx={{ borderRadius: 2 }}>
                    Replace
                  </Button>
                </Box>
              )}
              <IconButton
                onClick={handleRemovePhoto}
                color="error"
                sx={{ backgroundColor: '#ffebee', '&:hover': { backgroundColor: '#ffcdd2' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}

          {photoUploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Uploading photo...</Typography>
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 2, mt: 3, backgroundColor: '#fff8e1', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#e65100', mb: 2 }}>
            Additional Photos (Optional)
          </Typography>

          <Box component="label" sx={{ cursor: 'pointer', display: 'inline-block' }}>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.webp"
              multiple
              onChange={handleExtraPhotosUpload}
              style={{ display: 'none' }}
            />
            <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} sx={{ borderRadius: 2 }}>
              Select Photos
            </Button>
          </Box>

          {extraPhotoPreviews.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {extraPhotoPreviews.length} photo(s) ready to upload (saved when you click “Save Changes”).
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {extraPhotoPreviews.map((src, idx) => (
                  <Box
                    key={`${src}-${idx}`}
                    component="img"
                    src={src}
                    alt={`Extra ${idx + 1}`}
                    sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0' }}
                  />
                ))}
              </Box>
              <Button onClick={clearExtraPhotos} sx={{ mt: 1.5 }} color="inherit">
                Clear selection
              </Button>
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 2, mt: 3, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon /> Invoice Attachment
          </Typography>

          <Box
            component="label"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #4caf50',
              borderRadius: 2,
              p: 2.5,
              cursor: 'pointer',
              backgroundColor: '#f1f8e9',
              '&:hover': { backgroundColor: '#dcedc8' }
            }}
          >
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf"
              onChange={handleInvoiceUpload}
              style={{ display: 'none' }}
            />
            <CloudUploadIcon sx={{ fontSize: 36, color: '#4caf50', mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              Click to add invoice attachments (JPEG, PNG, GIF, PDF - Max 10MB each)
            </Typography>
          </Box>

          {(existingInvoices.length > 0 || invoiceFiles.length > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mt: 2 }}>
              {existingInvoices.map((invoiceUrl, index) => (
                <Box key={`${invoiceUrl}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1, bgcolor: '#fff', borderRadius: 2, border: '1px solid #dce8df' }}>
                  <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} href={invoiceUrl} target="_blank" sx={{ borderRadius: 2, fontWeight: 800 }}>
                    {invoiceDisplayName(invoiceUrl, `Invoice ${index + 1}`)}
                  </Button>
                  <IconButton size="small" onClick={() => handleRemoveExistingInvoice(invoiceUrl)} color="error" sx={{ backgroundColor: '#ffebee' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {invoiceFiles.map((file, index) => (
                <Box key={`${file.name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1, bgcolor: '#fff', borderRadius: 2, border: '1px solid #dce8df' }}>
                  {invoicePreviews[index] && invoicePreviews[index] !== 'pdf' ? (
                    <Box component="img" src={invoicePreviews[index]} alt="Invoice preview" sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }} />
                  ) : (
                    <ReceiptIcon sx={{ color: '#d32f2f', fontSize: 30 }} />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>{file.name}</Typography>
                  <IconButton size="small" onClick={() => handleRemovePendingInvoice(index)} color="error" sx={{ backgroundColor: '#ffebee' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {uploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Uploading invoice...</Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #66bb6a 0%, #2e7d32 100%)' }
          }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
